/**
 * People/Workforce Phase 4 — Policy assignment, governance publication and
 * Code of Conduct convergence helpers.
 *
 * Source contract: docs/source-of-truth/people-workforce-phase4-policy-conduct-contract.md
 * SQL owner: supabase/migrations/20270405000000_people_workforce_phase4_policy_conduct_convergence.sql
 *
 * Claim level: SOURCE_CONTRACT only. Not live-applied. Not PRODUCTION_SAFE.
 */

import { WORKFORCE_CAPABILITY_ROLE_GRANTS } from '@/lib/workforce/phase1a-capabilities';
import { roleHasWorkforceCapability } from '@/lib/workforce/phase2-people';

export const PHASE4_POLICY_CONDUCT_ARCHITECTURE_SHA =
  '5f3cd7e2637c46b53d2bcb3ed3dfbb64b0db62debbc0af018fbadb2280858728';

export const PHASE4_POLICY_CONDUCT_MIGRATION =
  'supabase/migrations/20270405000000_people_workforce_phase4_policy_conduct_convergence.sql';

/** Canonical manager Policies & Conduct workbench. */
export const POLICIES_CONDUCT_WORKBENCH_HREF = '/people/policies-conduct';
export const POLICIES_CONDUCT_PUBLICATIONS_HREF = '/people/policies-conduct/publications';
export const POLICIES_CONDUCT_ASSIGNMENTS_HREF = '/people/policies-conduct/assignments';
export const POLICIES_CONDUCT_CODE_HREF = '/people/policies-conduct/code';
export const POLICIES_CONDUCT_CASES_HREF = '/people/policies-conduct/cases';

export const POLICY_ASSIGNMENT_STATES = [
  'ASSIGNED',
  'ACKNOWLEDGED',
  'IMPORTED_MIGRATED',
  'WAIVED',
  'NOT_APPLICABLE',
  'OVERDUE',
  'TRAINING_DUE',
  'TRAINING_COMPLETE',
  'FOLLOW_UP_OPEN',
  'UNABLE_TO_DETERMINE',
] as const;

export type PolicyAssignmentState = (typeof POLICY_ASSIGNMENT_STATES)[number];

export const PUBLICATION_STATES = ['PUBLISHED', 'PUBLICATION_GAP', 'NO_RECEIPT'] as const;
export type PublicationState = (typeof PUBLICATION_STATES)[number];

export const ASSIGNMENT_PUBLICATION_STATES = ['RECEIPT_CURRENT', 'LEGACY_UNBRIDGED'] as const;
export type AssignmentPublicationState = (typeof ASSIGNMENT_PUBLICATION_STATES)[number];

export type PoliciesConductWorkbenchItem = {
  governance_policy_id: string;
  policy_name: string;
  governance_category: string | null;
  governance_version: string;
  governance_status: string;
  publication_receipt_id: string | null;
  workplace_policy_id: string | null;
  workplace_policy_code: string | null;
  workplace_policy_version: number | null;
  publication_state: PublicationState | string;
  href: string | null;
};

export type PoliciesConductWorkbenchResult =
  | {
      ok: true;
      as_of: string;
      timezone: string;
      page: number;
      page_size: number;
      total_count: number;
      page_count: number;
      status_counts: Record<string, number>;
      publication_gaps: number;
      unbridged_assignment_count: number;
      acknowledgement_exceptions: number;
      training_exceptions: number;
      failed_reminder_events: number;
      items: PoliciesConductWorkbenchItem[];
      capabilities: {
        can_govern: boolean;
        can_assign: boolean;
        can_manage_conduct: boolean;
      };
      notes: string[];
    }
  | {
      ok: false;
      error: string;
      code?: string;
      denied?: boolean;
      degraded?: boolean;
    };

export type PolicyAssignmentItem = {
  requirement_id: string;
  staff_id: string;
  worker_name: string;
  policy_id: string;
  policy_code: string | null;
  policy_title: string | null;
  policy_version: number | null;
  requirement_status: string;
  completion_source: string | null;
  projected_state: PolicyAssignmentState | string;
  due_at: string | null;
  publication_receipt_id: string | null;
  receipt_is_current: boolean;
  publication_state: AssignmentPublicationState | string;
  last_reminder_status: string | null;
  last_reminder_error: string | null;
  worker_action_url: string | null;
  worker_href: string | null;
};

export type PolicyAssignmentsResult =
  | {
      ok: true;
      as_of: string;
      timezone: string;
      page: number;
      page_size: number;
      total_count: number;
      page_count: number;
      status_counts: Record<string, number>;
      items: PolicyAssignmentItem[];
      notes: string[];
    }
  | {
      ok: false;
      error: string;
      code?: string;
      denied?: boolean;
      degraded?: boolean;
    };

export type CodeBundleResult =
  | {
      ok: true;
      as_of: string;
      timezone: string;
      has_current_code: boolean;
      receipt: Record<string, unknown> | null;
      workplace_policy?: Record<string, unknown> | null;
      acknowledgement_count: number;
      assigned_count: number;
      imported_count: number;
      training_due_count: number;
      training_complete_count: number;
      acknowledged_training_due_count: number;
      follow_up_open_count: number | null;
      counts_are_separate: true;
      no_single_compliance_percentage: true;
      capabilities?: { can_manage_conduct: boolean };
      notes: string[];
    }
  | {
      ok: false;
      error: string;
      code?: string;
      denied?: boolean;
      degraded?: boolean;
    };

export type WorkerPoliciesConductResult =
  | {
      ok: true;
      as_of: string;
      timezone: string;
      staff_id: string;
      viewer_mode: 'self' | 'manager';
      items: Array<Record<string, unknown>>;
      historical_items: Array<Record<string, unknown>>;
      historical_total_count: number;
      case_pointer: { open_case_count: number; href: string } | null;
      notes: string[];
    }
  | {
      ok: false;
      error: string;
      code?: string;
      denied?: boolean;
      degraded?: boolean;
    };

export const POLICY_VIEW_ROLES = WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'];
export const POLICY_GOVERN_ROLES = WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.policy.govern'];
export const POLICY_ASSIGN_ROLES = WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.policy.assign'];
export const CONDUCT_MANAGE_ROLES = WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.conduct.manage'];

export function canViewPoliciesConductWorkbench(role: string): boolean {
  return (
    roleHasWorkforceCapability(role, 'workforce.people.view') ||
    roleHasWorkforceCapability(role, 'workforce.policy.assign') ||
    roleHasWorkforceCapability(role, 'workforce.policy.govern')
  );
}

export function canGovernPolicies(role: string): boolean {
  return roleHasWorkforceCapability(role, 'workforce.policy.govern');
}

export function canAssignPolicies(role: string): boolean {
  return roleHasWorkforceCapability(role, 'workforce.policy.assign');
}

export function canManageConductCases(role: string): boolean {
  return roleHasWorkforceCapability(role, 'workforce.conduct.manage');
}

/**
 * Standalone historical conduct acknowledgements never alone make current Code compliant.
 */
export function legacyConductAckAloneMakesCodeCompliant(): boolean {
  return false;
}

/**
 * Never invent a single Code compliance percentage that hides separate states.
 */
export function codeCompliancePercentage(_bundle: {
  acknowledgement_count?: number;
  training_complete_count?: number;
  assigned_count?: number;
}): null {
  void _bundle;
  return null;
}

export function peoplePoliciesConductPath(
  filters: Record<string, string | number | undefined | null> = {},
  base: string = POLICIES_CONDUCT_WORKBENCH_HREF,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function workerPoliciesConductHref(staffId: string): string {
  return `/people/workers/${staffId}?tab=policies-conduct`;
}

export function policyAssignmentStateBadgeClass(state: string): string {
  switch (String(state).toUpperCase()) {
    case 'OVERDUE':
    case 'FOLLOW_UP_OPEN':
      return 'bg-status-danger-bg text-status-danger-fg border-status-danger-border';
    case 'ASSIGNED':
    case 'TRAINING_DUE':
      return 'bg-status-warning-bg text-status-warning-fg border-status-warning-border';
    case 'ACKNOWLEDGED':
    case 'TRAINING_COMPLETE':
      return 'bg-status-success-bg text-status-success-fg border-status-success-border';
    case 'IMPORTED_MIGRATED':
      return 'bg-violet-50 text-violet-800 border-violet-200';
    case 'WAIVED':
    case 'NOT_APPLICABLE':
      return 'bg-status-neutral-bg text-status-neutral-fg border-status-neutral-border';
    case 'PUBLICATION_GAP':
      return 'bg-rose-50 text-rose-800 border-rose-200';
    default:
      return 'bg-status-neutral-bg text-status-neutral-fg border-status-neutral-border';
  }
}

export const PHASE4_PRIMARY_NAV = {
  policiesConduct: {
    label: 'Policies & Conduct',
    href: POLICIES_CONDUCT_WORKBENCH_HREF,
    description:
      'Governance publication receipts, worker delivery, Code bundle and restricted cases',
  },
} as const;

/** Explicit non-claims mirrored for tests and UI copy. */
export const PHASE4_NON_CLAIMS = [
  'SOURCE_CONTRACT only',
  'Not live-applied',
  'Not PRODUCTION_SAFE',
  'Not SQL execution evidence',
  'Not live parity with linked remote',
  'Not provider adoption or UAT',
  'Not quantitative route retirement completion',
  'Not a global worker compliant/ready badge',
  'Not single Code compliance percentage',
  'Not dispatcher runtime evidence',
  'Not mobile app-version retirement evidence',
] as const;
